import { normalizeHttpUrl, type ChannelType } from "@/lib/agents/messaging/types"

/** Only the states worth spelling out — `connected` is carried by the green dot on the row. */
export const CHANNEL_STATUS_COLORS: Record<string, string> = {
  disconnected: "bg-gray-100 text-gray-800",
  error: "bg-red-100 text-red-800",
  token_expired: "bg-yellow-100 text-yellow-800",
}

/**
 * Display order: channels in the order they were connected, with the website widgets last —
 * they carry the bulky install block and are not where operators look first.
 */
export function sortChannelsForDisplay<T extends { channel_type: string; created_at?: string }>(
  channels: T[],
): T[] {
  const rank = (type: string) => (type === "web_demo" ? 2 : type === "web_widget" ? 1 : 0)
  return [...channels].sort(
    (a, b) =>
      rank(a.channel_type) - rank(b.channel_type) ||
      (a.created_at ?? "").localeCompare(b.created_at ?? ""),
  )
}

/**
 * Public-facing URL for a connected channel, or `null` when the channel has no
 * external surface to open:
 *  - Telegram  → the bot chat (`t.me/<handle>`), which opens the Telegram app.
 *    The handle is stored as `@username` in `account_name`.
 *  - Instagram → the business profile (`instagram.com/<handle>`); the bare
 *    handle is stored in `account_name`.
 *  - Facebook  → the business page (`facebook.com/<pageId>`); only the numeric
 *    page id is persisted (no vanity), and Facebook resolves it to the page.
 *  - TikTok    → the business profile (`tiktok.com/@<handle>`); how `account_name`
 *    gets filled is not settled yet, so treat it as possibly `null` or as a
 *    display name rather than a handle.
 *  - web_widget / web_demo → the site where the widget is embedded, taken from
 *    the operator-supplied `config.siteUrl` (informational); `null` until set.
 */
export function getChannelExternalUrl(channel: {
  channel_type: ChannelType
  account_name: string | null
  external_account_id: string | null
  config?: Record<string, unknown> | null
}): string | null {
  const handle = channel.account_name?.replace(/^@/, "").trim()
  switch (channel.channel_type) {
    case "telegram":
      return handle ? `https://t.me/${handle}` : null
    case "instagram":
      // account_name falls back to the display name when the IG username is
      // absent; only link when it actually looks like a handle (no spaces),
      // otherwise we'd emit a broken `instagram.com/<display name>` URL.
      return handle && /^[a-zA-Z0-9._]{1,30}$/.test(handle)
        ? `https://instagram.com/${handle}`
        : null
    case "facebook":
      return channel.external_account_id
        ? `https://facebook.com/${channel.external_account_id}`
        : null
    case "tiktok":
      // account_name may be null, or hold a display name rather than a
      // username; only link when it looks like a handle (TikTok usernames:
      // letters, digits, '.' and '_', up to 24 chars).
      return handle && /^[a-zA-Z0-9._]{1,24}$/.test(handle)
        ? `https://www.tiktok.com/@${handle}`
        : null
    case "web_widget":
    case "web_demo":
      // Operator-supplied; normalize/validate so a stale or scheme-less value
      // never yields a broken href (shares the write-path normalizer).
      return typeof channel.config?.siteUrl === "string"
        ? normalizeHttpUrl(channel.config.siteUrl)
        : null
    default:
      return null
  }
}
